"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { Check, Plus, X } from "lucide-react";
import { cn } from "@/utils/cn";
const avatarVariants = cva("relative flex shrink-0 overflow-hidden rounded-full", {
  variants: {
    size: {
      sm: "size-8",
      default: "size-10",
      lg: "size-14",
      xl: "size-16"
    }
  },
  defaultVariants: {
    size: "default"
  }
});
interface AvatarProps extends React.ComponentProps<typeof AvatarPrimitive.Root>, VariantProps<typeof avatarVariants> {}
function Avatar(t0) {
  const $ = _c(10);
  let className;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
  }
  let t1;
  if ($[4] !== className || $[5] !== size) {
    t1 = cn(avatarVariants({
      size,
      className
    }));
    $[4] = className;
    $[5] = size;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== props || $[8] !== t1) {
    t2 = <AvatarPrimitive.Root data-slot="avatar" className={t1} {...props} />;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function AvatarImage(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("aspect-square size-full", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AvatarPrimitive.Image data-slot="avatar-image" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function AvatarFallback(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-muted flex size-full items-center justify-center rounded-full text-sm font-medium", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AvatarPrimitive.Fallback data-slot="avatar-fallback" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// Status indicator variants
const statusVariants = cva("absolute flex items-center justify-center rounded-full border-2 border-background", {
  variants: {
    variant: {
      online: "bg-success-600",
      offline: "bg-neutral-400",
      away: "bg-warning-600",
      busy: "bg-error-600",
      check: "bg-primary text-primary-foreground",
      plus: "bg-neutral-200 text-neutral-600",
      close: "bg-error-600 text-error-foreground"
    },
    position: {
      "top-right": "top-0 right-0",
      "bottom-right": "bottom-0 right-0",
      "top-left": "top-0 left-0",
      "bottom-left": "bottom-0 left-0"
    },
    size: {
      sm: "size-3",
      default: "size-4",
      lg: "size-5"
    }
  },
  defaultVariants: {
    variant: "online",
    position: "top-right",
    size: "default"
  }
});
interface AvatarStatusProps extends VariantProps<typeof statusVariants> {
  className?: string;
}
function AvatarStatus(t0) {
  const $ = _c(16);
  const {
    className,
    variant,
    position,
    size
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== position || $[2] !== size || $[3] !== variant) {
    t1 = cn(statusVariants({
      variant,
      position,
      size,
      className
    }));
    $[0] = className;
    $[1] = position;
    $[2] = size;
    $[3] = variant;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== variant) {
    t2 = variant === "check" && <Check className="size-2.5" />;
    $[5] = variant;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== variant) {
    t3 = variant === "plus" && <Plus className="size-2.5" />;
    $[7] = variant;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== variant) {
    t4 = variant === "close" && <X className="size-2.5" />;
    $[9] = variant;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <div className={t1}>{t2}{t3}{t4}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}

// Avatar Group component
interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  limit?: number;
  total?: number;
}
function AvatarGroup(t0) {
  const $ = _c(24);
  let children;
  let className;
  let props;
  let t1;
  let total;
  if ($[0] !== t0) {
    ({
      children,
      className,
      limit: t1,
      total,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = total;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    total = $[5];
  }
  const limit = t1 === undefined ? 4 : t1;
  let remaining;
  let t2;
  let t3;
  let t4;
  if ($[6] !== children || $[7] !== className || $[8] !== limit || $[9] !== props || $[10] !== total) {
    const childrenArray = React.Children.toArray(children);
    const visibleChildren = childrenArray.slice(0, limit);
    remaining = total ? total - limit : childrenArray.length - limit;
    if ($[15] !== className) {
      t2 = cn("flex items-center -space-x-2", className);
      $[15] = className;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    t3 = props;
    t4 = visibleChildren.map(_temp);
    $[6] = children;
    $[7] = className;
    $[8] = limit;
    $[9] = props;
    $[10] = total;
    $[11] = remaining;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    remaining = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
  }
  let t5;
  if ($[17] !== remaining) {
    t5 = remaining > 0 && <Avatar className="border-2 border-background bg-primary text-primary-foreground"><AvatarFallback>+{remaining}</AvatarFallback></Avatar>;
    $[17] = remaining;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className={t2} {...t3}>{t4}{t5}</div>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
function _temp(child, index) {
  return <div key={index} className="relative">{child}</div>;
}
export { Avatar, AvatarImage, AvatarFallback, AvatarStatus, AvatarGroup };